import React, { useState, useEffect, useCallback } from 'react';
import DateDayNavigator from './DateDayNavigator';

export default function ChangeLogTab({ api, user, selectedBase, globalView }) {
  // Helper to get local date in YYYY-MM-DD
  const getLocalDate = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [date, setDate] = useState(getLocalDate());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper: get YYYY-MM-DD in local time from any timestamp
  const localYMD = (ts) => {
    const d = new Date(ts);
    const Y = d.getFullYear();
    const M = String(d.getMonth() + 1).padStart(2, '0');
    const D = String(d.getDate()).padStart(2, '0');
    return `${Y}-${M}-${D}`;
  };
  
  // Helper: get HH:MM:SS in local time
  const localHMS = (ts) => {
    const d = new Date(ts);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const fetchAlerts = useCallback(async () => {
    if (!selectedBase) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const locKey = selectedBase.replace(/\s+/g, '_');
      // Use '/alerts' because baseURL already ends with '/api'
      const res = await api.get('/alerts', {
        params: {
          email: user.email,
          location: selectedBase,
          global: user.is_superuser && globalView ? true : undefined
        }
      });

      const payload = res.data;
      const allAlerts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.alerts)
          ? payload.alerts
          : [];

      const filtered = allAlerts.filter(a => localYMD(a.timestamp) === date);

      setEntries(filtered);
    } catch (err) {
      console.error('Failed to load alerts:', err);
      setError('Unable to load change log.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [api, selectedBase, date, globalView, user]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div>
      {/* Date picker */}
      <div style={{ marginBottom: 12 }}>
        <DateDayNavigator id="changeLogDate" value={date} onChange={setDate} />
      </div>

      {/* Error & Retry */}
      {error && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ color: 'red' }}>{error}</p>
          <button onClick={fetchAlerts}>Retry</button>
        </div>
      )}

      {/* Main content: loading, table, or “no entries” */}
      {loading ? (
        <p>Loading change log...</p>
      ) : entries.length > 0 ? (
        <table className="change-log-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Tail #</th>
              <th>Action</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => {
              // safe extract of time portion, supporting both ISO and local formats
              const rawTs = e.timestamp || '';
              let time;
              if (rawTs.includes('T')) {
                // ISO: "YYYY-MM-DDTHH:MM:SS(.sss)"
                time = rawTs.split('T')[1].split('.')[0];
              } else {
                // local: "YYYY-MM-DD HH:MM:SS"
                time = rawTs.split(' ')[1] || '';
              }
              // Clean details and split into key:value parts, handling comma or space before keys
              const rawDetails = String(e.details || '')
                .replace(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, '')
                .replace(/id=/gi, '')
                .trim()
                .replace(/=/g, ':')
                .replace(/,\s*/g, ',');

              // Split on comma or space preceding a key (e.g. ' new_key:')
              const parts = rawDetails
                .split(/,\s*|\s+(?=\w+:)/g)
                .map(p => p.trim())
                .filter(Boolean);

              return (
                <tr key={idx}>
                  <td style={{ whiteSpace: 'nowrap' }}>{localHMS(rawTs)}</td>
                  <td>{e.user}</td>
                  <td>{e.tail}</td>
                  <td>{e.action}</td>
                  <td className="change-log-details-cell">
                    {parts.map((part, i) => {
                      const [key, ...rest] = part.split(':');
                      const val = rest.join(':').trim();
                      return (
                        <span key={i} className="change-log-detail-part">
                          <strong>{key}:</strong> {val}
                          {i < parts.length - 1 ? ', ' : ''}
                        </span>
                      );
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>No changes recorded for {date}.</p>
      )}
    </div>
  );
}
